/**
 * Seconds shaved off a JWT's own `exp` so the token is renewed slightly before the server stops
 * accepting it.
 *
 * This is a clock-skew allowance, not a token lifetime — the lifetime always comes from the token or
 * from `AppConfig.tokenExpiration` (PAUL-3246). Half a minute covers the drift seen between a box-office
 * PC and the API without meaningfully shortening a five-minute token.
 */
const CLOCK_SKEW_SECONDS = 30;

/**
 * The moment a JWT stops being usable, as a millisecond epoch, or `null` when the token does not say.
 *
 * `null` is the answer for every token this cannot vouch for — an opaque (non-JWT) token, a malformed
 * one, a payload without a numeric `exp`, or an `exp` that has already passed. Callers are expected to
 * fall back to their configured lifetime, which is what keeps the non-Pauly backends working: several of
 * them issue tokens that are not JWTs at all.
 *
 * Hand-rolled rather than pulled from a library: the payload is the middle base64url segment, and this
 * is the whole of what is needed to read it. Note it does **not** verify the signature and must not be
 * used to decide anything but when to renew — the server remains the authority on whether a token is
 * good.
 */
export function getJwtExpiration(token: string | null | undefined): number | null {
  const payload = decodePayload(token);
  const exp = payload?.['exp'];

  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return null;
  }

  const expiration = (exp - CLOCK_SKEW_SECONDS) * 1000;

  /* An already-lapsed token is reported as unknown rather than as expired: writing a past expiry would
     have the caller treat a freshly issued token as due on arrival, and a renewal loop is worse than a
     stale clock. */
  return expiration > Date.now() ? expiration : null;
}

function decodePayload(token: string | null | undefined): Record<string, unknown> | null {
  const segment = token?.split('.')[1];

  if (!segment || token.split('.').length !== 3) {
    return null;
  }

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    /* No UTF-8 decode step: `atob` leaves any multi-byte claim (a name, say) as mojibake, which
       `JSON.parse` still parses happily — and `exp` is a number, so it is unaffected either way. */
    const parsed = JSON.parse(atob(padded));

    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    /* Not a JWT, or not one this can read. The caller's configured lifetime applies. */
    return null;
  }
}
